import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import Cookies from 'js-cookie';
import { reduce } from 'lodash';
import { Observable, throwError, BehaviorSubject, timer, of } from 'rxjs';
import { tap, distinctUntilChanged, switchMap, catchError, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';

import { env } from '@envs/env';
import { config } from '@config/config';
import { UrlUtils } from '@app/utils/url.utils';
import { Envelope } from '@app/utils/flume-api.interceptor';
import { JwtService } from '@app/utils/jwt.service';
import { AuthState, AuthStatus, ChangeStatus, SetAuthData } from '@app/auth/auth.state';
import { User } from '@app/models/users/users.model';
import { trackEvent } from '@app/utils/amplitude.utils';

interface TokenData {
    token_type: string;
    access_token: string;
    expires_in: number;
    refresh_token: string;
}

@Injectable({
    providedIn: 'root',
})
export class OAuthService {
    authEvents$: Observable<AuthStatus>;

    timer$ = timer(0, env.auth.interval);
    trackEvent = trackEvent;

    /** Same proactive refresh window as supportal (seconds before access token expiry). */
    private readonly accessTokenRefreshBufferSeconds = 5 * 60;

    constructor(private http: HttpClient, private jwt: JwtService, private store: Store) {
        this.init();
    }

    // set up and watch authevents to trigger login acitons
    public init() {
        this.authEvents$ = this.getAuthEvents();
        this.startAuthInterval();

        const token: string = this.getAccessToken();
        if (token) {
            this.setAccessToken(token);
        }
        // start watching
        this.subscribeToAuthEvents();
    }

    public getAuthEvents() {
        return this.store.select(AuthState.status).pipe(distinctUntilChanged());
    }

    public startAuthInterval() {
        this.timer$.pipe(switchMap(() => this.updateAuthState())).subscribe();
    }

    // watch auth events and update auth status
    public subscribeToAuthEvents() {
        this.authEvents$.subscribe((status) => {
            if (status === AuthStatus.AUTH_VALID) {
                const token: string = this.getAccessToken();
                this.http.get(this.jwt.getBaseUrl(token)).subscribe((res: Envelope<User>) => {
                    this.store.dispatch(
                        new SetAuthData(this.jwt.getBaseUrl(token), this.jwt.getScopes(token), res.data[0])
                    );
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_COMPLETE));
                });
            }
        });
    }

    public updateAuthState() {
        const at = this.getAccessToken();
        const rt = this.getRefreshToken();
        const tokens = {
            at: at === '' ? false : true,
            rt: rt === '' ? false : true,
        };

        return this.authEvents$.pipe(
            take(1),
            tap((status) => {
                if (tokens.at === false && tokens.rt === false) {
                    this.completeAuthorizationCodeFlow().subscribe({
                        error: (err) => {
                            let error;
                            if (typeof err === 'string') {
                                error = err;
                            } else if (err && err.error && err.error.detailed[0]) {
                                if (err.error.message === 'unauthorized_client') {
                                    error = 'Access Denied.';
                                } else {
                                    error = err.error.detailed[0];
                                }
                            }
                            this.initAuthorizationCodeFlow(error);
                        },
                    });
                } else if (tokens.at === false && tokens.rt === true) {
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_NO_ACCESS));
                    this.refreshAccessToken().subscribe();
                } else if (tokens.at === true && tokens.rt === false && status !== AuthStatus.AUTH_COMPLETE) {
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_NO_REFRESH));
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
                } else if (
                    this.jwt.isTokenExpired(
                        this.jwt.decodeToken(this.getAccessToken()),
                        this.accessTokenRefreshBufferSeconds
                    )
                ) {
                    this.refreshAccessToken().subscribe();
                } else if (status !== AuthStatus.AUTH_COMPLETE) {
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
                } else {
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_COMPLETE));
                }
            })
        );
    }

    public isLoggedIn(): boolean {
        return !!this.getAccessToken();
    }

    public initAuthorizationCodeFlow(error?: string) {
        this.trackEvent('Auth Init Login');
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));

        const state = this.generateState();
        this.saveState(state);

        const loginUrl = this.getStaticLoginUrl(state, error);
        if (!env.testing) {
            window.location.href = loginUrl;
        }
    }

    // Navigate to static login page
    public getStaticLoginUrl(state, error) {
        let url = this.generateRedirectUrl(state, window.location.origin);
        if (error) {
            url += `&error=${error}`;
        }
        return url;
    }

    public generateRedirectUrl(state, uri) {
        const url =
            `${env.apiUrl}/account/login?client_id=${env.auth.clientId}` +
            `&redirect_uri=${encodeURIComponent(uri)}&state=${state}&response_type=code`;
        return url;
    }

    public completeAuthorizationCodeFlow(): Observable<any> {
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_IN_PROGRESS));

        const params = UrlUtils.getUrlParams();

        if (!params.code || !params.state) {
            return throwError(null);
        }

        // Compare before clearing. Cookie/session was cleared on a prior attempt if the auth
        // interval fired again while the token exchange was still in flight (timer every 5s),
        // which produced a false "Invalid State".
        const storedState = this.peekState();
        if (params.state !== storedState) {
            trackEvent('Auth Invalid State');
            return throwError('Invalid State');
        }

        // Consume the callback URL immediately so repeat interval ticks do not re-enter with
        // code/state still in the address bar after stored state was cleared.
        this.stripOAuthParamsFromUrl();

        this.getAndClearState();

        return this.http
            .post<Envelope<TokenData>>(`${env.apiUrl}/oauth/token`, {
                client_id: env.auth.clientId,
                code: params.code,
                grant_type: 'authorization_code',
                redirect_uri: window.location.origin,
            })
            .pipe(
                tap(
                    (res) => {
                        trackEvent('Auth Login Successful');
                        const tokens = res.data[0];
                        this.saveAccessToken(tokens.access_token, tokens.expires_in);
                        this.saveRefreshToken(tokens.refresh_token);

                        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
                    },
                    () => {
                        trackEvent('Auth Login Unsuccessful');
                        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
                    }
                )
            );
    }

    public refreshAccessToken(): Observable<any> {
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_REFRESHING));

        const authParams = `grant_type=refresh_token&client_id=${
            env.auth.clientId
        }&refresh_token=${this.getRefreshToken()}`;

        return this.http
            .post<Envelope<TokenData>>(`${env.apiUrl}/oauth/token`, encodeURI(authParams), {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, // eslint-disable-line @typescript-eslint/indent
            }) // eslint-disable-line @typescript-eslint/indent
            .pipe(
                tap(
                    (res) => {
                        trackEvent('Auth Token Refreshed');
                        const tokens = res.data[0];
                        this.saveAccessToken(tokens.access_token, tokens.expires_in);
                        this.saveRefreshToken(tokens.refresh_token);

                        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
                    },
                    () => {
                        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
                    }
                )
            );
    }

    /**
     * Revokes the session on the server (Bearer access token + JSON body with refresh_token).
     * Clears local tokens and starts login only after the request finishes.
     */
    public logout(): Observable<Envelope<unknown> | null> {
        const rt = this.getRefreshToken();
        const request$ = rt
            ? this.http.post<Envelope<unknown>>(
                  `${env.apiUrl}/oauth/logout`,
                  { refresh_token: rt },
                  {
                      headers: { 'Content-Type': 'application/json' },
                  }
              )
            : of(null);

        return request$.pipe(
            catchError(() => of(null)),
            tap(() => {
                this.clearTokens();
                this.initAuthorizationCodeFlow();
            })
        );
    }

    /**
     * Generates a random value for OAuth state
     * @returns Series of hexadecimal characters
     */
    public getAccessToken(): string {
        return Cookies.get(env.auth.accessTokenName) || '';
    }

    public getRefreshToken(): string {
        return Cookies.get(env.auth.refreshTokenName) || '';
    }

    public clearTokens() {
        const opts = this.getAuthCookieOpts();
        Cookies.remove(env.auth.accessTokenName, opts);
        Cookies.remove(env.auth.refreshTokenName, opts);
    }

    public setAccessToken(tok: string) {
        this.store.dispatch(
            new SetAuthData(this.jwt.getBaseUrl(tok), this.jwt.getScopes(tok), { id: this.jwt.getTokenId(tok) })
        );
    }

    protected generateState(): string {
        // Length is halved because there are 2 hex chars in 1 uint8
        const arr = new Uint8Array(config.authStateLength / 2);
        // Generate random values (msCrypto for IE 11)
        if ((window as any).msCrypto) {
            (window as any).msCrypto.getRandomValues(arr);
        } else {
            window.crypto.getRandomValues(arr);
        }
        // Converts random values to hexadecimal array, then joins into a string
        return reduce(arr, (acc, val) => acc + ('0' + val.toString(16)).slice(-2), '');
    }

    protected getBaseHost() {
        const parts = window.location.hostname.split('.');
        return [parts[parts.length - 2], parts[parts.length - 1]].join('.');
    }

    /** Shared attributes so set/remove stay in sync (incl. SameSite for CSRF-style hardening). */
    protected getAuthCookieOpts(): Cookies.CookieAttributes {
        return {
            secure: env.auth.https,
            domain: env.auth.https ? `.${this.getBaseHost()}` : 'localhost',
            sameSite: 'lax',
        };
    }

    protected saveAccessToken(token: string, expiresIn: number) {
        Cookies.set(env.auth.accessTokenName, token, {
            ...this.getAuthCookieOpts(),
            expires: new Date(Date.now() + expiresIn * 1000),
        });
    }

    protected saveRefreshToken(token: string) {
        Cookies.set(env.auth.refreshTokenName, token, {
            ...this.getAuthCookieOpts(),
            expires: env.auth.refreshTokenExpires,
        });
    }

    protected saveState(state: string) {
        // Per-tab storage avoids cross-tab races where a second login overwrites the shared
        // oauth_state cookie and the first tab returns with a mismatched state.
        sessionStorage.setItem(env.auth.stateTokenName, state);
    }

    protected peekState(): string {
        return sessionStorage.getItem(env.auth.stateTokenName) || Cookies.get(env.auth.stateTokenName) || '';
    }

    protected getAndClearState() {
        const key = env.auth.stateTokenName;
        const state = sessionStorage.getItem(key) || Cookies.get(key) || '';
        sessionStorage.removeItem(key);
        Cookies.remove(key, this.getAuthCookieOpts());
        return state;
    }

    /** Removes OAuth callback params so UrlUtils and repeat auth polling do not see a stale code/state pair. */
    protected stripOAuthParamsFromUrl() {
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            const path = url.pathname + url.search + url.hash;
            window.history.replaceState(window.history.state, document.title, path);
        } catch {
            // ignore replaceState errors in non-browser or exotic URLs
        }
    }
}
